import { useCallback, useEffect, useState } from 'react';

/**
 * Checks if the browser's state matches the provided `mediaQuery`.
 *
 * Handles updating via the provided `callback` if the browser's state changes related to the media query.
 */
function useMediaQueryEffect(
  mediaQuery: string,
  callback: (matches: boolean) => void
) {
  useEffect(() => {
    if (window && window.matchMedia) {
      const mql = window.matchMedia(mediaQuery);
      callback(mql.matches);

      const updateCallback = e => {
        callback(e.matches);
      };

      // As usual web specs are dumb and we have two ways to add events listeners...
      // So addEventListener is correct and addListener is deprecated
      if (mql.addEventListener) {
        mql.addEventListener('change', updateCallback);
      } else if (mql.addListener) {
        mql.addListener(updateCallback);
      }

      return () => {
        if (mql.removeEventListener) {
          mql.removeEventListener('change', updateCallback);
        } else if (mql.removeListener) {
          mql.removeListener(updateCallback);
        }
      };
    }
  }, [callback, mediaQuery]);
}

/**
 * Returns a boolean indicating if the browser's state matches the supplied {@link mediaQuery}.
 */
export function useMediaQuery(mediaQuery: string) {
  const [doesMatch, setDoesMatch] = useState(false);

  const handleMediaQueryUpdate = useCallback(
    matches => setDoesMatch(matches),
    [setDoesMatch]
  );
  useMediaQueryEffect(mediaQuery, handleMediaQueryUpdate);

  return doesMatch;
}
